<script setup lang="ts">
import { cloneDeep } from 'lodash-es';
import Menu from 'primevue/menu';
import { computed, onMounted, ref, shallowRef } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';

import ColumnList from './ColumnList.vue';
import { CustomColumnProps, DragableColumn } from './CustomColumn.vue.d';
import { setVisibilityByMaxColumn } from './helpers/setVisibilityColumn.helper';
import Button from '../button/Button.vue';
import { TableColumn } from '../datatable/DataTable.vue.d';
import readConfig from '../datatable/helpers/readConfig.helper';

const props = withDefaults(defineProps<CustomColumnProps>(), { type: 'menu' });

const emit = defineEmits<{
  'update:visibleColumns': [columns: TableColumn[]];
}>();

onMounted(() => loadConfig());

const { locale } = useWangsVueConfig('CustomColumn');

const columnList = ref<InstanceType<typeof ColumnList>>();
const visibilityMenu = ref<Menu>();
/**
 * Stores full columns config for reordering and toggling visibility
 * Will be passed for columnReorderData on ColumnList
 */
const savedColumns = shallowRef<DragableColumn[]>([]);

const visibilityMenuId = computed<string>(
  () => 'column-visibility-menu-' + props.tableId,
);

const columnVisibilityModel = computed({
  get: () => props.visibleColumns,
  set: (cols: TableColumn[]) => {
    emit(
      'update:visibleColumns',
      cols.filter((col) => col.visible != false),
    );
  },
});

const toggleMenu = async (event: Event): Promise<void> => {
  await loadConfig();

  if (visibilityMenu.value && 'toggle' in visibilityMenu.value) {
    const eventTarget = (event.currentTarget ?? event.target) as HTMLElement;

    const currentTarget =
      eventTarget.tagName.toLowerCase() === 'th'
        ? eventTarget
        : eventTarget.parentElement;

    const toggleEvent: Event = {
      ...event,
      currentTarget,
    };

    visibilityMenu.value.toggle(toggleEvent);
  }
};

const loadConfig = async (): Promise<void> => {
  const columns = await readConfig(
    props.tableId,
    cloneDeep(props.defaultColumns),
  );

  savedColumns.value = setVisibilityByMaxColumn(
    columns,
    props.maxColumn,
    columns.filter((col) => col.visible !== false).length,
  );

  columnVisibilityModel.value = savedColumns.value.filter(
    (col) => col.visible !== false,
  );
};

defineExpose({
  toggleMenu,
  resetToDefault: () => {
    columnList.value?.resetToDefault();
  },
});
</script>

<template>
  <Menu
    :id="visibilityMenuId"
    ref="visibilityMenu"
    v-if="props.type === 'menu'"
    :data-table-id="tableId"
    :pt="{
      root: {
        class: [...$preset.menu?.root.class, 'shadow-panel'],
      },
      start: {
        class: [
          ...$preset.menu?.start.class,
          $preset.customcolumn?.menustart.class,
        ],
      },
    }"
    @hide="columnList?.setColumnVisibilityConfig"
    append-to="body"
    auto-z-index
    data-wv-name="column-visibility"
    data-wv-section="menu"
    popup
  >
    <template #start>
      <Button
        :label="locale.resetDefaultText"
        @click="columnList?.resetToDefault"
        data-wv-section="button-reset-default"
        text
      />
    </template>

    <template #end>
      <ColumnList
        ref="columnList"
        v-model="columnVisibilityModel"
        v-bind="props"
        :saved-columns="savedColumns"
        data-wv-section="custom-column-list"
      />
    </template>
  </Menu>

  <ColumnList
    ref="columnList"
    v-if="type === 'flat'"
    v-model="columnVisibilityModel"
    v-bind="props"
    :class="[
      { 'grid grid-rows-4 grid-flow-col-dense w-max gap-x-4': type === 'flat' },
    ]"
    :saved-columns="savedColumns"
    data-wv-section="custom-column-flat-list"
  />
</template>
